import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Helmet } from 'react-helmet-async';
import Card from './Card';
import CategoryLinks from './CategoryLinks';
import LoadingSpinner from '../common/LoadingSpinner';
import ErrorAlert from '../common/ErrorAlert';
import { EVENT, HERO, PRIME_TRIAL_URL, PRIME_DAY_PATH, countdownLabel } from '../../utils/primeDay';
import './PrimeDayPage.css';

const SITE_NAME = 'Lite Pocket Deals';

/** Re-renders every 30 s so the countdown stays current. */
const useCountdown = () => {
  const [label, setLabel] = useState(() => countdownLabel());
  useEffect(() => {
    const id = setInterval(() => setLabel(countdownLabel()), 30000);
    return () => clearInterval(id);
  }, []);
  return label;
};

const Section = ({ section }) => (
  <section className="prime-day__section" id={section.id} aria-labelledby={`${section.id}-title`}>
    <div className="prime-day__section-head">
      <h2 id={`${section.id}-title`}>{section.title}</h2>
      {section.seeAll && <Link className="prime-day__see-all" to={section.seeAll}>See all →</Link>}
    </div>
    {section.description && <p className="prime-day__section-desc">{section.description}</p>}
    <div className="deal-grid">
      {section.deals.map((deal) => <Card key={deal._id} {...deal} />)}
    </div>
  </section>
);

Section.propTypes = {
  section: PropTypes.shape({
    id: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    description: PropTypes.string,
    seeAll: PropTypes.string,
    deals: PropTypes.arrayOf(PropTypes.object).isRequired,
  }).isRequired,
};

/**
 * /prime-big-deal-days. The hero is pre-rendered by the server (primeDayShell in
 * lib/prerender.js) with the same markup; the sections come from /api/deals/prime-day.
 */
const PrimeDayPage = () => {
  const countdown = useCountdown();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let active = true;
    setError(null);
    axios.get('/api/deals/prime-day')
      .then((res) => active && setData(res.data))
      .catch(() => active && setError('We couldn’t load the Prime Day deals. Please try again.'));
    return () => { active = false; };
  }, [attempt]);

  const { origin } = window.location;
  const url = `${origin}${PRIME_DAY_PATH}`;

  return (
    <div className="container prime-day">
      <header className="prime-day__hero">
        <p className="prime-day__eyebrow">{HERO.eyebrow}</p>
        <h1 className="prime-day__title">{HERO.title}</h1>
        <p className="prime-day__intro">{HERO.intro}</p>
        <p className="prime-day__countdown">{countdown}</p>
        <a className="prime-day__cta" href={PRIME_TRIAL_URL} target="_blank" rel="nofollow sponsored noopener noreferrer">Try Prime free for 30 days</a>
      </header>

      {/* Same <head> the server renders (lib/pageRoutes.js primeDayHead); Helmet replaces it */}
      <Helmet>
        <title>{`${EVENT.name} ${EVENT.year} Deals (Oct 6–7) | ${SITE_NAME}`}</title>
        <meta name="description" content={`${EVENT.name} ${EVENT.year} runs ${EVENT.datesLabel}. ${HERO.intro}`} />
        <link rel="canonical" href={url} />
        {data && (
          <script type="application/ld+json">
            {JSON.stringify({
              '@context': 'https://schema.org',
              '@type': 'FAQPage',
              mainEntity: data.faq.map(({ q, a }) => ({ '@type': 'Question', name: q, acceptedAnswer: { '@type': 'Answer', text: a } })),
            })}
          </script>
        )}
      </Helmet>

      {data && data.sections.length > 0 && (
        <nav className="prime-day__jump" aria-label="Jump to section">
          {data.sections.map((s) => <a key={s.id} href={`#${s.id}`}>{s.title}</a>)}
        </nav>
      )}

      {!data && !error && <LoadingSpinner />}
      {error && <ErrorAlert error={error} title="Something went wrong" onRetry={() => setAttempt((n) => n + 1)} />}
      {data && data.sections.map((section) => <Section key={section.id} section={section} />)}

      {data && (
        <section className="prime-day__faq" aria-labelledby="prime-day-faq-title">
          <h2 id="prime-day-faq-title">{EVENT.name} questions</h2>
          {data.faq.map(({ q, a }) => (
            <details key={q}>
              <summary>{q}</summary>
              <p>{a}</p>
            </details>
          ))}
        </section>
      )}

      {data && <CategoryLinks />}
    </div>
  );
};

export default PrimeDayPage;
